import type { CrmFiltersModel } from "app/pages/sales-intelligence/domain/crm-integration/types/custom-filter-values";
import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import { crmFilterKeyPrefix } from "app/pages/sales-intelligence/domain/crm-integration/utils/crm-filter-key";
import {
    crmFieldsToModel,
    modelToCrmFields,
} from "app/pages/sales-intelligence/domain/crm-integration/utils/custom-filters-dto-adapters";
import type { ValueToDtoCategory } from "app/pages/sales-intelligence/domain/search/types/filter-dto";
import {
    filterDtoToPanelValue,
    filterPanelValueToDto,
} from "app/pages/sales-intelligence/domain/search/utils/filter-dto-adapters";
import { isSignalTypeKey } from "app/pages/sales-intelligence/domain/signals/utils/signal-key";
import {
    assembleSignalsDTO,
    splitSignalsDTO,
} from "app/pages/sales-intelligence/domain/signals/utils/signals-dto-adapter";

import { leadsFilterApiCategory } from "../types/leadsFilterApiCategory";
import type {
    ILeadsSearchFiltersDto,
    LeadsSearchFilterApiCategory,
    LeadsSearchAllFiltersTypes,
} from "../types/leadsSearchFilters.dto";
import { providerToApiCategory } from "../types/leadsSearchFilters.dto";

const crmProviderEntries = Object.entries(providerToApiCategory) as ReadonlyArray<
    [CrmIntegrationProvider, LeadsSearchFilterApiCategory]
>;

const crmDtoToPanelTransforms = Object.fromEntries(
    crmProviderEntries.map(([provider, apiCategory]) => [
        apiCategory,
        (dto: CrmFiltersModel) => modelToCrmFields(dto, provider),
    ]),
);

const crmPanelToDtoTransforms = Object.fromEntries(
    crmProviderEntries.map(([provider, apiCategory]) => {
        const prefix = crmFilterKeyPrefix(provider);

        return [
            apiCategory,
            {
                belongsTo: (key: keyof LeadsSearchAllFiltersTypes) => (key as string).startsWith(prefix),
                assemble: (bucket: Partial<LeadsSearchAllFiltersTypes>) => crmFieldsToModel(bucket, provider),
            },
        ];
    }),
);

const leadsDtoToPanelTransforms = {
    signalsFilters: splitSignalsDTO,
    ...crmDtoToPanelTransforms,
} satisfies Partial<{
    [K in keyof ILeadsSearchFiltersDto]: (
        value: NonNullable<ILeadsSearchFiltersDto[K]>,
    ) => Partial<LeadsSearchAllFiltersTypes>;
}>;

const leadsPanelToDtoTransforms = {
    signalsFilters: { belongsTo: isSignalTypeKey, assemble: assembleSignalsDTO },
    ...crmPanelToDtoTransforms,
} satisfies Partial<{
    [K in keyof ILeadsSearchFiltersDto]: ValueToDtoCategory<
        LeadsSearchAllFiltersTypes,
        ILeadsSearchFiltersDto,
        keyof ILeadsSearchFiltersDto
    >;
}>;

export const leadsFilterDtoToPanelValue = (dto: Partial<ILeadsSearchFiltersDto>): Partial<LeadsSearchAllFiltersTypes> =>
    filterDtoToPanelValue<LeadsSearchAllFiltersTypes, ILeadsSearchFiltersDto>(dto, leadsDtoToPanelTransforms);

/**
 * Folds a leads-panel value back into the leads-filter DTO. Symmetric to `leadsFilterDtoToPanelValue`, and
 * total for the same set of keys: routing comes from the static `leadsFilterApiCategory` table, so a filter
 * that is merely *hidden* — flag off, category-scoped out, key-hidden, or its config still `pending` — still
 * persists under its own category instead of being hoisted to the DTO's top level.
 */
export const leadsFilterPanelValueToDto = (
    filterValue: Partial<LeadsSearchAllFiltersTypes>,
): Partial<ILeadsSearchFiltersDto> =>
    filterPanelValueToDto<LeadsSearchAllFiltersTypes, ILeadsSearchFiltersDto>(
        filterValue,
        leadsFilterApiCategory,
        leadsPanelToDtoTransforms,
    );
